import React, { createRef, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import { useTheme } from '@material-ui/core/styles'
import PropTypes from 'prop-types'

import { errorMsgFontSize, defaultErrorMsgFixedHeight } from './constants'
import { Label } from '../Controls'
import { YDivider } from '../../styles'
import { clearFormStatus } from '../../redux'
import { t } from 'i18next'

export const getDefaultValue = fieldTemplate => {
  if (fieldTemplate !== undefined) {
    if (typeof fieldTemplate.defaultValue === 'function') {
      return fieldTemplate.defaultValue()
    } else {
      return fieldTemplate.defaultValue
    }
  }
}

const findFieldObject = (data, fieldKey) => {
  const fields = fieldKey.split('.')
  if (!fields || fields.length === 0) {
    return undefined
  }
  let obj = data
  let i
  for (i = 0; i < fields.length - 1; ++i) {
    obj = obj[fields[i]]
    if (obj === undefined) {
      return undefined
    }
  }
  return { obj, field: fields[i] }
}

/**
 * Returns the value at the input key inside the data input object.  
 *
 * @param {object} data The data object to retrieve the data
 * @param {string} fieldKey The key for the field. This has the format 'key1.key2.key3'.
 * @param {string} defaultEmpty The default value if the field is undefined.
 * @returns {any} The data at the input key.
 */
export const getField = (data, fieldKey, defaultEmpty = '') => {
  let ret
  if (fieldKey) {
    ret = findFieldObject(data, fieldKey)
  }
  if (ret === undefined || ret.obj[ret.field] === undefined) {
    return defaultEmpty
  }
  return ret.obj[ret.field]
}

/**
 * Sets the value at the input key inside the data input object.  
 *
 * @param {object} data The data object to retrieve the data
 * @param {string} fieldKey The key for the field. This has the format 'key1.key2.key3'.
 * @param {string} value The value to input into the input data object.
 * @returns {any} The data at the input key.
 */
export const setField = (data, fieldKey, value) => {
  const fields = fieldKey.split('.')
  if (!fields || fields.length === 0) {
    return null
  }
  let obj = data
  let i
  for (i = 0; i < fields.length - 1; ++i) {
    if (obj[fields[i]] === undefined) {
      obj[fields[i]] = {}
    }
    obj = obj[fields[i]]
  }
  obj[fields[i]] = value
}

export const handleGetFormFieldData = (data, template) => {
  const fieldKey = template.getFieldKey ? template.getFieldKey : template.fieldKey
  const defaultValue = getDefaultValue(template)
  let field = defaultValue
  if (template.arrKeyInBackendObject) {
    const arr = getField(data, template.arrKeyInBackendObject)
    if (arr && arr.length && template.arrIndexInBackendObject !== undefined) {
      field = getField(arr[template.arrIndexInBackendObject], fieldKey, defaultValue)
    }
  } else {
    field = getField(data, fieldKey, defaultValue)
  }
  if (template.onAfterGet) {
    field = template.onAfterGet(field, data)
  }
  return field
}

export const handlePostFormFieldData = (postObject, template, value, refs) => {
  const fieldKey = template.postFieldKey ? template.postFieldKey : template.fieldKey
  if (fieldKey) {
    if (template.onBeforePost) {
      value = template.onBeforePost(value, refs)
    }
    if (template.arrKeyInBackendObject) {
      if (!Array.isArray(postObject[template.arrKeyInBackendObject])) {
        let maxIndex = -1
        for (let i = 0; i < refs.length; ++i) {
          if (refs[i].current?.getFormTemplate) {
            if (refs[i].current.getFormTemplate().arrIndexInBackendObject > maxIndex) {
              maxIndex = refs[i].current.getFormTemplate().arrIndexInBackendObject
            }
          }
        }
        postObject[template.arrKeyInBackendObject] = new Array(maxIndex + 1)
        for (let i = 0; i < maxIndex + 1; ++i) {
          postObject[template.arrKeyInBackendObject][i] = {}
        }
      }
      postObject[template.arrKeyInBackendObject][template.arrIndexInBackendObject][fieldKey] = value
    } else {
      setField(postObject, fieldKey, value)
    }
  }
}

export const clearForm = arrRefs => {
  arrRefs.forEach(e => { if (e.current !== null) e.current.clear() })
}

export const validateForm = arrRefs => {
  const postObject = {}
  let isError = false
  for (let i = 0; i < arrRefs.length; ++i) {
    if (arrRefs[i].current && arrRefs[i].current.validate) {
      const result = arrRefs[i].current.validate(postObject, arrRefs)
      isError = isError || result.isError
    }
  }
  if (isError) {
    return null
  }
  return postObject
}

/**
 * Adds a formtemplate and associated control to arrRefs to dynamically extend the form controls on screen.
 * This can also be know when the form can change based on user interactions.
 *
 * @param {object} param object parameter.
 * @param {object} param.fieldTemplate fieldTemplate entry. This is not an arry but an object.
 * @param {React.node} param.Component Form field component from the FormControls directory.
 * @param {object} param.props props for the component if any.
 * @param {Array} param.arrRefs arrRefs.
 * @returns {React.node} The Component with all the connections needed.
 */
export const addFormTemplate = ({ fieldTemplate, Component, props = {}, arrRefs = [] }) => {
  const ref = createRef()
  arrRefs.push(ref)
  return (
    <Component
      key={arrRefs.length}
      ref={ref} 
      fieldTemplate={fieldTemplate}
      {...props}
    />
  )
}

/**
 * Adds formtemplates and associated controls to arrRefs to dynamically extend the form controls on screen.
 * This can also be know when the form can change based on user interactions.
 *
 * @param {Array} arrTemplates array of templates with shape {fieldTemplate, Component, props}.
 * @param {Array} arrRefs arrRefs.
 * @returns {Array<React.node>} The Components to display on screen. These are completely wired into the forms manager. Display in the render as {componentsArray}.
 */
export const addFormTemplates = (arrTemplates, arrRefs = []) => {
  const componentsArray = []
  for (let i = 0; i < arrTemplates.length; ++i) {
    const arrTemplatesEntry = arrTemplates[i]
    componentsArray.push(addFormTemplate({ fieldTemplate: arrTemplatesEntry.fieldTemplate, Component: arrTemplatesEntry.Component, props: arrTemplatesEntry.props, arrRefs }))
  }
  return componentsArray
}

export const addFormTemplateRow = ({ data, rowFieldTemplates, rowComponents, arrRefs, arrKeyInBackendObject, arrIndexInBackendObject }) => {
  if (!(rowFieldTemplates.length === rowComponents.length)) {
    throw new Error('addFormTemplateRow: Array arguments must match in length.')
  }
  const row = []
  const rowArrRefs = []
  for (let i = 0; i < rowFieldTemplates.length; ++i) {
    const fieldTemplate = { ...rowFieldTemplates[i] }
    fieldTemplate.arrKeyInBackendObject = arrKeyInBackendObject
    fieldTemplate.arrIndexInBackendObject = arrIndexInBackendObject
    const Component = rowComponents[i].component
    const ref = createRef()
    arrRefs.push(ref)
    rowArrRefs.push(ref)
    row.push(
      <Component
        key={i}
        ref={ref} 
        fieldTemplate={fieldTemplate}
        {...rowComponents[i].props}
        data={data}
      />
    )
  }
  return { row, rowArrRefs }
}

export const handleRequired = props => {
  let error = ''
  if (props.noTranslate) {
    error = 'Required'
  } else {
    error = t(props.fieldTemplate.isRequiredError ? props.fieldTemplate.isRequiredError : 'forms.required')
  }
  return error
}

export const ErrorMessage = props => {
  const { errorMsg, errorMsgFixedHeight, styles = {} } = props
  const theme = useTheme()

  const reservedHeight = errorMsgFixedHeight || defaultErrorMsgFixedHeight
  const labelStyle = {
    color: theme.palette.error.light,
    fontSize: errorMsgFontSize,
    ...(errorMsg
      ? {
          height: 'auto',
          minHeight: errorMsgFixedHeight === '0px' ? defaultErrorMsgFixedHeight : reservedHeight,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: 1.4,
        }
      : { height: reservedHeight }),
    ...styles
  }
  return (
    <>
      {errorMsg 
        ? <Label noTranslate style={labelStyle}>
          {errorMsg}
        </Label>
        : <YDivider height={errorMsgFixedHeight} />
    }
    </>
  )
}

ErrorMessage.propTypes = {
  errorMsg: PropTypes.string,
  errorMsgFixedHeight: PropTypes.string,
  styles: PropTypes.object
}

export const useFormBase = props => {
  const { fixedErrorMessageFieldHeight, fieldTemplate, data, label } = props
  const refFormElement = useRef()
  const valueRef = useRef(getDefaultValue(fieldTemplate))
  const errorMsgFixedHeight = fixedErrorMessageFieldHeight ? defaultErrorMsgFixedHeight : '0px'
  const [errorMsg, setErrorMsg] = useState('')
  const [disabled, setDisabled] = useState(fieldTemplate?.disabled)
  const fieldLabel = useRef(label || fieldTemplate?.label)

  useEffect(() => {
    if (!data || Object.keys(data).length === 0) {
      const val = getDefaultValue(fieldTemplate)
      refFormElement.current.setValue(val)
      valueRef.current = val
      return
    }
    const fielddata = handleGetFormFieldData(data, fieldTemplate)
    refFormElement.current.setValue(fielddata)
    valueRef.current = fielddata
  }, [data])

  useEffect(() => {
    return () => {
      setErrorMsg && setErrorMsg('')
      clearFormStatus()
    }
  }, [])

  const onChange = val => {
    valueRef.current = val
    props.onChange && props.onChange(val)
    if (errorMsg) {
      setErrorMsg && setErrorMsg('')
      clearFormStatus()
    }
  }
  return { onChange, errorMsgFixedHeight, disabled, setDisabled, fieldLabel, refFormElement, valueRef, errorMsg, setErrorMsg }
}

export const formImperativeHandleFunctions = (fieldTemplate, refFormElement, valueRef, setErrorMsg, setDisabled) => {
  return {
    value: valueRef.current,
    focus: () => refFormElement.current.focus(),
    getValue: () => {
      return valueRef.current
    },
    setValue: val => {
      refFormElement.current.setValue(val)
      valueRef.current = val
    },
    clear: () => { 
      refFormElement.current.setValue(getDefaultValue(fieldTemplate))
      setDisabled(fieldTemplate.disabled)
      valueRef.current = getDefaultValue(fieldTemplate)
      setErrorMsg && setErrorMsg('')
    },
    setDisabled: value => setDisabled(value),
    setErrorMsg: (error, noTranslate) => setErrorMsg(noTranslate ? error : t(error)),
    clearErrorMsg: () => setErrorMsg && setErrorMsg(''),
    getFormTemplate: () => fieldTemplate,
    setFieldType: (value) => {
      if (value) {
        refFormElement.current.setFieldType(value)
      }
    }
  }
}

export const handleFormPostObject = (postObject, fieldTemplate, value, refs, error, setErrorMsg) => {
  if (setErrorMsg) {
    setErrorMsg(error)
    const isError = error !== ''
    if (isError) {
      return { isError }
    }
  }
  return { value: handlePostFormFieldData(postObject, fieldTemplate, value, refs) }
}

export const buildPostObject = (arrRefs, formTemplate, arrKeyInBackendObject) => {
  let postObject = validateForm(arrRefs)
  if (postObject !== null) {
    // Used to change the post object data of individual controls. 
    if (formTemplate) {
      for (let i = 0; i < formTemplate.length; ++i) {
        if (typeof formTemplate[i].onBeforePost === 'function') {
          postObject = formTemplate[i].onBeforePost(postObject, arrRefs[i].current)
        }
      }
    }
    if (Array.isArray(postObject[arrKeyInBackendObject])) {
      const arr = []
      postObject[arrKeyInBackendObject].forEach(e => {
        if (Object.keys(e).length > 0) {
          arr.push(e)
        }
      })
      postObject[arrKeyInBackendObject] = arr
    }
  }
  return postObject
}

export const convertToFormComponent = ReactComponent => {
  return forwardRef((props, ref) => {
    // eslint-disable-next-line react/prop-types
    const { fieldTemplate, data, ...finalProps } = props
    useImperativeHandle(ref, () => ({
      getFormTemplate: () => fieldTemplate
    }))
    return <ReactComponent {...finalProps} />
  })
}